import {
  LitElement,
  paths,
  html,
  unsafeHTML,
  css,
  utils,
  Vimeo,
} from '@hh/global.js'
const { externalVideoEmbedSrc, getPreviousSibling } = utils

class AdaptiveVideo extends LitElement {
  static properties = {
    videoObject: {
      type: Object,
      attribute: 'video-object',
    },
    id: {
      type: String,
    },
    idMobile: {
      type: String,
      attribute: 'id-mobile',
    },
    title: {
      type: String,
    },
    autoplay: {
      type: Boolean,
    },
    controls: {
      type: Boolean,
    },
    loop: {
      type: Boolean,
    },
    host: {
      type: String,
    },
    hostMobile: {
      type: String,
      attribute: 'host-mobile',
    },
    description: {
      type: String,
    },
    aspectRatio: {
      type: String,
      attribute: 'aspect-ratio',
    },
    posterImage: {
      type: String,
      attribute: 'poster-image',
    },
    videoFit: {
      type: String,
      attribute: 'video-fit',
    },
  }

  get computedAspectRatio() {
    return (
      this.videoObject?.aspect_ratio ||
      (this.aspectRatio === 'wide' && 16 / 9) ||
      (this.aspectRatio === 'full' && 4 / 3) ||
      eval(this.aspectRatio) ||
      16 / 9
    )
  }

  get videoElement() {
    return this.renderRoot?.getElementById('video-element') ?? null
  }

  get breakpoint() {
    return '(min-width: 750px)'
  }

  get iframeVideoUrl() {
    if (
      this.idMobile &&
      this.hostMobile &&
      !window.matchMedia(this.breakpoint).matches
    ) {
      return `${
        externalVideoEmbedSrc(
          this.hostMobile,
          this.idMobile,
          this.autoplay,
          this.controls,
          this.loop
        ) +
        `${this.hostMobile === 'vimeo' && this.autoplay ? '&background=1&autoplay=1&loop=1&byline=0&title=0' : ''}`
      }`
    } else {
      return `${
        externalVideoEmbedSrc(
          this.host,
          this.id,
          this.autoplay,
          this.controls,
          this.loop
        ) +
        `${this.host === 'vimeo' && this.autoplay ? '&background=1&autoplay=1&loop=1&byline=0&title=0' : ''}`
      }`
    }
  }

  constructor() {
    super()
  }

  connectedCallback() {
    super.connectedCallback()
    this.playing = this.autoplay
    this.style.setProperty('--hh-aspect-ratio', this.computedAspectRatio)
    this.style.setProperty('--hh-video-fit', this.videoFit)
    window
      .matchMedia(this.breakpoint)
      .addEventListener('change', this.onMediaChange.bind(this))
  }

  onMediaChange() {
    if (
      (this.host || this.hostMobile) &&
      this.videoElement.src != this.iframeVideoUrl
    ) {
      this.videoElement.src = this.iframeVideoUrl
    }
  }

  firstUpdated() {
    if (this.host === 'vimeo' && this.autoplay) {
      this.videoElement.src = this.iframeVideoUrl
    }
  }

  handlePlayClick() {
    if (this.playing == false) {
      this.playing = true
    }
    let overlay = this.renderRoot?.querySelector('.video-overlay')
    if (overlay) overlay.style.display = 'none'
    this.videoElement.play()
  }

  generatePosterWithText() {
    if (!this.posterImage) return html``
    return html`
      <div class="video-overlay">
        ${this.title ? html`<h2>${this.title}</h2>` : html``}
        ${this.description ? html`<p>${this.description}</p>` : html``}
        <button class="video-poster-button" @click="${this.handlePlayClick}">
          ${unsafeHTML(paths.play || html`►`)}
        </button>
        <img src="${this.posterImage}" />
      </div>
    `
  }

  renderVideo() {
    return html`
      <video
        id="video-element"
        playsinline="playsinline"
        ?autoplay="${this.autoplay}"
        ?controls="${this.controls}"
        ?loop="${this.loop}"
        ${this.posterImage ? `poster="${this.posterImage}"` : html``}
        preload="none"
        muted="muted"
        aria-label="${this.description}"
      >
        ${this.videoObject.sources.map(
          (source) =>
            html`<source src="${source.url}" type="${source.mime_type}"></source>`
        )}
      </video>
    `
  }

  renderIframe() {
    return html`
      <iframe
        id="video-element"
        frameborder="0"
        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
        allowfullscreen="allowfullscreen"
        loading="lazy"
        src="${this.iframeVideoUrl}"
        title="${this.title}"
      >
      </iframe>
    `
  }

  render() {
    return html`
      ${['vimeo', 'youtube'].includes(this.host)
        ? this.renderIframe()
        : this.renderVideo()}
      ${this.autoplay ? html`` : this.generatePosterWithText()}
    `
  }

  static styles = css`
    :host {
      aspect-ratio: var(--hh-aspect-ratio, 16 / 9);
      display: block;
      position: relative;
    }
    #video-element {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
      object-fit: var(--hh-video-fit, contain);
    }
    .video-overlay {
      position: absolute;
      display: flex;
      top: 0;
      width: 100%;
      height: 100%;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }
    h2,
    p {
      z-index: 1;
      color: white;
    }
    p {
      width: 25%;
      text-align: center;
      margin-bottom: 3rem;
    }
    .video-overlay img {
      position: absolute;
      top: 0;
      width: 100%;
      height: 100%;
      filter: brightness(0.5);
    }
    .video-poster-button {
      align-items: center;
      background: transparent;
      border: 1px solid white;
      border-radius: 60px;
      cursor: pointer;
      width: 60px;
      height: 60px;
      padding: 1rem;
      display: flex;
      justify-content: center;
      z-index: 1;
    }
    .video-poster-button svg {
      position: relative;
      width: 30px;
      height: 30px;
      fill: white;
      stroke: white;
    }
  `
}

window.customElements.define('hh-adaptive-video', AdaptiveVideo)
